import {
  CardCvcElement,
  CardExpiryElement,
  CardNumberElement,
} from '@stripe/react-stripe-js';
import {
  type StripeElementClasses,
  type StripeElementStyle,
} from '@stripe/stripe-js';
import { useTranslation } from 'react-i18next';

import { TranslationEntries } from '@service-initiation/tech-care-data-access-i18n';

import { type CCErrors } from '../types';
import { AUIImitationTextField } from './AUIImitationTextField';
import { PaymentRow } from './SharedStyledComponents';

const CARD_ELEMENT_CLASSES: StripeElementClasses = {
  base: 'si-stripe-base',
  complete: 'si-stripe-complete',
  empty: 'si-stripe-empty',
  focus: 'si-stripe-focused',
  invalid: 'si-stripe-invalid',
};

const CARD_ELEMENT_STYLE: StripeElementStyle = {
  base: {
    '::placeholder': {
      color: 'rgb(110, 118, 125)',
    },
    fontFamily: 'Apercu Pro, Lato, "Lucida Grande", Tahoma, Sans-Serif',
    fontSize: '16px',
    fontSmoothing: 'antialiased',
    fontWeight: 400,
    lineHeight: '1.15',
  },
  invalid: {
    color: '#fa755a',
    iconColor: '#fa755a',
  },
};

export type StripeSectionProps = {
  ccErrors: CCErrors;
  setErrors: (value: CCErrors) => void;
};

const TEXT = TranslationEntries.Repairs.BillingDetails;
export const StripeSection: React.FC<StripeSectionProps> = ({
  ccErrors: { CCCVC: ccCvcError, CCExp: ccExpError, CCNumber: ccNumberError },
  setErrors,
}) => {
  const { t } = useTranslation();

  const setStripeErrors = (ccNumber: string, ccExp: string, ccCvc: string) => {
    setErrors({
      CCCVC: ccCvc,
      CCExp: ccExp,
      CCNumber: ccNumber,
    });
  };

  return (
    <>
      <PaymentRow>
        <AUIImitationTextField
          error={ccNumberError}
          label={t(TEXT.StripeCCNumberLabel)}
        >
          <span data-testid="billing-details-card-number">
            <CardNumberElement
              className="fs-mask"
              onChange={(e) => {
                if (e.error?.message || e.empty) {
                  setStripeErrors(
                    e.error?.message || 'Value is required',
                    ccExpError,
                    ccCvcError
                  );
                } else {
                  setStripeErrors('', ccExpError, ccCvcError);
                }
              }}
              options={{
                classes: CARD_ELEMENT_CLASSES,
                placeholder: '',
                showIcon: true,
                style: CARD_ELEMENT_STYLE,
              }}
            />
          </span>
        </AUIImitationTextField>
      </PaymentRow>
      <PaymentRow>
        <AUIImitationTextField
          error={ccExpError}
          label={t(TEXT.StripeCCExpireLabel)}
        >
          <span data-testid="billing-details-exp-date">
            <CardExpiryElement
              className="fs-mask"
              onChange={(e) => {
                if (e.error?.message || e.empty) {
                  setStripeErrors(
                    ccNumberError,
                    e.error?.message || 'Value is required',
                    ccCvcError
                  );
                } else {
                  setStripeErrors(ccNumberError, '', ccCvcError);
                }
              }}
              options={{
                classes: CARD_ELEMENT_CLASSES,
                placeholder: '',
                style: CARD_ELEMENT_STYLE,
              }}
            />
          </span>
        </AUIImitationTextField>
        <AUIImitationTextField
          error={ccCvcError}
          label={t(TEXT.StripeCCCvcLabel)}
        >
          <span data-testid="billing-details-cvc-number">
            <CardCvcElement
              className="fs-mask"
              onChange={(e) => {
                if (e.error?.message || e.empty) {
                  setStripeErrors(
                    ccNumberError,
                    ccExpError,
                    e.error?.message || 'Value is required'
                  );
                } else {
                  setStripeErrors(ccNumberError, ccExpError, '');
                }
              }}
              options={{
                classes: CARD_ELEMENT_CLASSES,
                placeholder: '',
                style: CARD_ELEMENT_STYLE,
              }}
            />
          </span>
        </AUIImitationTextField>
      </PaymentRow>
    </>
  );
};
